import { useEffect, useRef, useState } from "react";
import { Video, RefreshCw, Check, X, Square, Circle } from "lucide-react";

const MAX_MB = 45;

function pickMimeType() {
  const candidates = ['video/mp4;codecs="avc1.42E01E,mp4a.40.2"', "video/mp4", "video/webm;codecs=vp9,opus", "video/webm", "video/3gpp"];
  return candidates.find((t) => window.MediaRecorder?.isTypeSupported?.(t)) || "";
}

export default function VideoRecorder({ onCapture, onClose, testid = "video-recorder" }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [preview, setPreview] = useState(null); // { url, blob, mime }
  const [error, setError] = useState(null);

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  useEffect(() => {
    let active = true;
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: "environment" }, audio: true })
      .then((stream) => {
        if (!active) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      })
      .catch(() => setError("Não foi possível acessar câmera e microfone. Verifique as permissões do navegador ou envie um vídeo da galeria."));
    return () => {
      active = false;
      clearInterval(timerRef.current);
      try { recorderRef.current?.stop(); } catch {}
      stopStream();
    };
  }, []);

  const start = () => {
    const stream = streamRef.current;
    if (!stream) return;
    const mime = pickMimeType();
    chunksRef.current = [];
    const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    recorderRef.current = rec;
    rec.ondataavailable = (e) => {
      if (e.data?.size) chunksRef.current.push(e.data);
    };
    rec.onstop = () => {
      const fullType = rec.mimeType || mime || "video/webm";
      const cleanType = fullType.split(";")[0]; // remove codecs (contém vírgula e corrompe o data URL)
      const blob = new Blob(chunksRef.current, { type: cleanType });
      if (blob.size > MAX_MB * 1024 * 1024) {
        setError(`O vídeo ficou com ${(blob.size / 1024 / 1024).toFixed(1)}MB e passa do limite de ${MAX_MB}MB. Grave um trecho mais curto.`);
        setPreview(null);
        return;
      }
      if (blob.size < 1024) {
        setError("A gravação ficou vazia. Tente novamente segurando alguns segundos a mais.");
        setPreview(null);
        return;
      }
      setPreview({ url: URL.createObjectURL(blob), blob, mime: cleanType });
    };
    rec.start(1000);
    setElapsed(0);
    setRecording(true);
    timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
  };

  const stop = () => {
    clearInterval(timerRef.current);
    setRecording(false);
    try { recorderRef.current?.stop(); } catch {}
  };

  const retake = () => {
    if (preview) URL.revokeObjectURL(preview.url);
    setPreview(null);
    setError(null);
  };

  const confirm = () => {
    if (!preview) return;
    const reader = new FileReader();
    reader.onload = () => {
      stopStream();
      onCapture(reader.result, preview.mime);
    };
    reader.readAsDataURL(preview.blob);
  };

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid={testid}>
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative bg-[#0D0D10] border border-white/10 rounded-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <p className="font-display font-bold flex items-center gap-2">
            <Video size={18} className="text-gold" /> Gravar Vídeo
          </p>
          <button type="button" onClick={onClose} data-testid={`${testid}-close`} className="text-zinc-400 hover:text-gold transition-colors" aria-label="Fechar câmera">
            <X size={20} />
          </button>
        </div>

        {error && !recording ? (
          <>
            <p className="text-sm text-zinc-400 border border-dashed border-white/15 rounded-xl p-6 text-center" data-testid={`${testid}-error`}>{error}</p>
            <button type="button" onClick={retake} data-testid={`${testid}-retry`}
              className="mt-4 w-full inline-flex items-center justify-center gap-2 font-mono text-xs tracking-widest border border-white/15 text-zinc-300 px-5 py-3 rounded-full hover:border-gold/50 hover:text-gold transition-colors">
              <RefreshCw size={13} /> TENTAR DE NOVO
            </button>
          </>
        ) : preview ? (
          <video src={preview.url} controls playsInline className="w-full rounded-xl border border-white/10 bg-black" data-testid={`${testid}-preview`} />
        ) : (
          <div className="relative">
            <video ref={videoRef} autoPlay playsInline muted className="w-full rounded-xl border border-white/10 bg-black" data-testid={`${testid}-video`} />
            {recording && (
              <span className="absolute top-3 left-3 inline-flex items-center gap-2 bg-black/70 rounded-full px-3 py-1.5 font-mono text-xs text-crimson" data-testid={`${testid}-timer`}>
                <span className="w-2 h-2 rounded-full bg-crimson animate-pulse" /> {fmt(elapsed)}
              </span>
            )}
          </div>
        )}

        {!error && (
          <div className="flex gap-3 mt-4">
            {preview ? (
              <>
                <button type="button" onClick={confirm} data-testid={`${testid}-confirm`}
                  className="flex-1 inline-flex items-center justify-center gap-2 font-display font-semibold bg-gold text-obsidian px-6 py-3 rounded-full hover:bg-gold-amber transition-colors">
                  <Check size={16} /> Usar este Vídeo
                </button>
                <button type="button" onClick={retake} data-testid={`${testid}-retake`}
                  className="inline-flex items-center gap-2 font-mono text-xs tracking-widest border border-white/15 text-zinc-400 px-5 py-3 rounded-full hover:border-gold/50 hover:text-gold transition-colors">
                  <RefreshCw size={13} /> GRAVAR OUTRO
                </button>
              </>
            ) : recording ? (
              <button type="button" onClick={stop} data-testid={`${testid}-stop`}
                className="flex-1 inline-flex items-center justify-center gap-2 font-display font-semibold bg-crimson text-white px-6 py-3 rounded-full hover:opacity-90 transition-opacity">
                <Square size={16} /> Parar ({fmt(elapsed)})
              </button>
            ) : (
              <button type="button" onClick={start} data-testid={`${testid}-start`}
                className="flex-1 inline-flex items-center justify-center gap-2 font-display font-semibold bg-gold text-obsidian px-6 py-3 rounded-full hover:bg-gold-amber transition-colors">
                <Circle size={16} className="fill-crimson text-crimson" /> Iniciar Gravação
              </button>
            )}
          </div>
        )}
        <p className="mt-3 font-mono text-[10px] text-zinc-600 text-center">VÍDEOS DE ATÉ {MAX_MB}MB</p>
      </div>
    </div>
  );
}
